import { IBreadcrumbItem, IStyle, PivotItem } from '@fluentui/react';
import { IH2OTheme, Pivot, useClassNames, useHaicPageTitle, useTheme } from '@h2oai/ui-kit';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import semver from 'semver';

import { HeaderPure } from '../../components/Header/Header';
import { FALLBACK_MLOPS_API_VERSION, useQueryMlopsApiVersion } from '../../discovery-service/discoveryService.hooks';
import { Job, Job_MonitoringState, Job_State } from '../../mlops/gen/ai/h2o/mlops/batch/v1/job_pb';
import { useMLOpsService } from '../../mlops/hooks';
import { useQueryMonitoringConfig } from '../../mlops/mlops.deployer.hooks';
import { GrpcStatusCode, isGrpcGatewayErrorResponse } from '../../services/HTTPResponse';
import { useEnv } from '../../utils/hooks';
import { ClassNamesFromIStyles } from '../../utils/models';
import { formatError } from '../../utils/utils';
import { LoaderView } from '../Orchestrator/WorkflowDetail';
import { useWorkspaces } from '../Orchestrator/WorkspaceProvider';
import { BatchScoringTabDetail, IBatchScoringTabDetailProps } from './BatchScoringTabDetail';
import { BatchScoringTabMonitoring, IBatchScoringTabMonitoringProps } from './BatchScoringTabMonitoring';
import { MLOpsError404Page } from './DeploymentDetail';
import { getIdFromName } from './DeploymentTabDetail';
import NavigationWrapper from './NavigationWrapper';
import PageWrapper from './PageWrapper';
import { useGranularMLOpsPermissions } from './userActions';

export type JobDetailParams = {
  job_id: string;
  workspace_id: string;
  tab_id: string;
  action: string;
  item_name: string;
};

/**
 * This enum gives a name to each tab in the pivot component. The values of the enum are indexes of the tabs.
 */
export enum JobTab {
  DETAILS = 0,
  MONITORING,
}

export const getStateProps = (state?: Job_State) => {
  switch (state) {
    case Job_State.STATE_UNSPECIFIED:
      return { name: 'Unspecified', color: 'var(--h2o-gray900)' };
    case Job_State.CREATED:
      return { name: 'Created', color: 'var(--h2o-yellow500)' };
    case Job_State.SUBMITTED:
      return { name: 'Submitted', color: 'var(--h2o-yellow500)' };
    case Job_State.SUBMISSION_FAILED:
      return { name: 'Submission failed', color: 'var(--h2o-red500)' };
    case Job_State.RUNNING:
      return { name: 'Running', color: 'var(--h2o-blue500)' };
    case Job_State.PENDING:
      return { name: 'Pending', color: 'var(--h2o-yellow500)' };
    case Job_State.FINISHED:
      return { name: 'Finished', color: 'var(--h2o-green500)' };
    case Job_State.FAILED:
      return { name: 'Failed', color: 'var(--h2o-red500)' };
    case Job_State.CANCELLED:
      return { name: 'Cancelled', color: 'var(--h2o-gray500)' };
    case Job_State.CANCELLING:
      return { name: 'Cancelling', color: 'var(--h2o-yellow900)' };
    case Job_State.TIMEOUT:
      return { name: 'Timed out', color: 'var(--h2o-red500)' };
    default:
      return { name: 'Unknown', color: 'var(--h2o-gray900)' };
  }
};

export const getMonitoringStateProps = (state?: Job_MonitoringState) => {
  switch (state) {
    case Job_MonitoringState.UNKNOWN:
      return { name: 'Unknown', color: 'var(--h2o-gray900)' };
    case Job_MonitoringState.NONE:
      return { name: 'None', color: 'var(--h2o-gray500)' };
    case Job_MonitoringState.PENDING:
      return { name: 'Pending', color: 'var(--h2o-yellow500)' };
    case Job_MonitoringState.RUNNING:
      return { name: 'Running', color: 'var(--h2o-blue500)' };
    case Job_MonitoringState.FINISHED:
      return { name: 'Finished', color: 'var(--h2o-green500)' };
    case Job_MonitoringState.FAILED:
      return { name: 'Failed', color: 'var(--h2o-red500)' };
    default:
      return { name: 'None', color: 'var(--h2o-gray500)' };
  }
};

export const FORM_SECTION_MAX_WIDTH = 820;

interface IBatchScoringDetailStyles {
  formSectionWrapper: IStyle;
  formSection: IStyle;
  formSectionTitle: IStyle;
}

export const batchScoringDetailStyles = (theme: IH2OTheme): IBatchScoringDetailStyles => ({
  formSectionWrapper: { marginTop: 12, marginBottom: 12 },
  formSectionTitle: {
    textTransform: 'uppercase',
    fontSize: 18,
    fontWeight: 600,
    paddingBottom: 6,
    marginTop: 6,
    marginBottom: 6,
  },
  formSection: {
    backgroundColor: theme.semanticColors?.contentBackground,
    borderRadius: 4,
    padding: 16,
    overflowY: 'auto',
  },
});

export const FormSection = (props: { title?: string; fullWidth?: boolean; children: React.ReactNode }) => {
  const theme = useTheme(),
    classNames = useClassNames<IBatchScoringDetailStyles, ClassNamesFromIStyles<IBatchScoringDetailStyles>>(
      'batchScoringDetail',
      batchScoringDetailStyles(theme)
    );

  return (
    <div className={classNames.formSectionWrapper}>
      {props?.title && <h3 className={classNames.formSectionTitle}>{props.title}</h3>}
      <div
        className={classNames.formSection}
        style={{
          maxWidth: props.fullWidth ? undefined : FORM_SECTION_MAX_WIDTH,
        }}
      >
        {props.children}
      </div>
    </div>
  );
};

export type JobDetailContentProps = {
  detailTabProps: IBatchScoringTabDetailProps;
  loading?: boolean;
  monitoringTabProps?: IBatchScoringTabMonitoringProps;
  onTabClick: (tab: JobTab) => void;
  selectedTab: JobTab;
  workspaceId: string;
};

export const JobDetailContent = (props: JobDetailContentProps) => {
  const { loading, monitoringTabProps, onTabClick } = props,
    workspaceName = React.useMemo(() => `workspaces/${props.workspaceId}`, [props.workspaceId]),
    permissions = useGranularMLOpsPermissions(workspaceName),
    onItemClick = React.useCallback(
      (item?: PivotItem) => {
        if (!item) return;
        onTabClick(Number(item.props.itemKey) as JobTab);
      },
      [onTabClick]
    ),
    flexStyles = {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
    },
    fillStyles = {
      pivotContainerStyles: {
        root: {
          '.ms-Pivot-wrapper': flexStyles,
          ...flexStyles,
        },
      },
      styles: {
        itemContainer: {
          '> div': flexStyles,
          ...flexStyles,
        },
        root: {
          margin: '0px 30px',
        },
      },
    };

  return (
    <Pivot
      {...fillStyles}
      onLinkClick={onItemClick}
      selectedKey={String(props.selectedTab || 0)}
      placeholder={undefined}
      items={[
        ...(permissions.hasAccessToBatchScoringDetailPage_create || permissions.hasAccessToBatchScoringDetailPage_view
          ? [
              {
                headerText: 'Details',
                content: loading ? (
                  <LoaderView loaderText="Loading job detail..." />
                ) : (
                  <BatchScoringTabDetail {...props.detailTabProps} />
                ),
              },
            ]
          : []),
        ...(monitoringTabProps && permissions.hasAccessToBatchScoringMonitoringTab
          ? [
              {
                headerText: 'Monitoring',
                content: loading ? (
                  <LoaderView loaderText="Loading monitoring UI..." />
                ) : (
                  <BatchScoringTabMonitoring {...monitoringTabProps} />
                ),
              },
            ]
          : []),
      ]}
    />
  );
};

const pathTabToJobTab = {
  '': JobTab.DETAILS,
  monitoring: JobTab.MONITORING,
} as const;

const BatchScoringDetail = () => {
  const params = useParams<JobDetailParams>();
  const { ACTIVE_WORKSPACE_NAME } = useWorkspaces();
  const isNew = params.job_id === 'start-new';
  const isBatchScoreModel = params.tab_id === 'from-model' && params.item_name;
  const mlopsService = useMLOpsService();
  const history = useHistory();
  const env = useEnv();

  const [job, setJob] = React.useState<Job>();
  const [loading, setLoading] = React.useState(true);
  const [showNotFound, setShowNotFound] = React.useState(false);

  const monitoringConfigQuery = useQueryMonitoringConfig({
    meta: {
      toastError: (err: unknown) => `Failed to fetch monitoring config: ${formatError(err)}`,
    },
  });
  const monitoringConfig = monitoringConfigQuery.data?.monitoringConfig;
  const monitoringGloballyDisabled = !monitoringConfig?.enabled;

  const fetchJob = React.useCallback(
    async (name: string) => {
      setLoading(true);
      try {
        const data = await mlopsService.getJob({ name });
        setJob(data?.job);
        setShowNotFound(false);
      } catch (err) {
        if (
          isGrpcGatewayErrorResponse(err) &&
          (err.code === GrpcStatusCode.NOT_FOUND ||
            err.code === GrpcStatusCode.INVALID_ARGUMENT ||
            err.code === GrpcStatusCode.INTERNAL)
        ) {
          setShowNotFound(true);
        }
      } finally {
        setLoading(false);
      }
    },
    [mlopsService]
  );

  React.useEffect(() => {
    if (params.job_id && !isNew && ACTIVE_WORKSPACE_NAME) {
      void fetchJob(`${ACTIVE_WORKSPACE_NAME}/jobs/${params.job_id}`);
    } else {
      setLoading(false);
    }
  }, [params.job_id, isNew, ACTIVE_WORKSPACE_NAME, fetchJob]);

  const experimentId = React.useMemo(() => {
    return job?.instanceSpec?.deploymentComposition?.experimentId;
  }, [job?.instanceSpec?.deploymentComposition?.experimentId]);

  const breadcrumbs: IBreadcrumbItem[] = [
    {
      key: `home`,
      text: `home`,
      onClick: () => history.push('/'),
    },
    {
      key: `mlops`,
      text: `MLOps`,
      onClick: () => history.push('/mlops'),
    },
    {
      key: 'jobs',
      text: 'Jobs',
      onClick: () => history.push(`/mlops/${ACTIVE_WORKSPACE_NAME}/jobs`),
    },
    {
      key: 'job',
      text: isNew ? 'Start new job' : job?.displayName || 'Job detail',
      isCurrentItem: true,
    },
  ];

  const detailTabProps: IBatchScoringTabDetailProps = {
    job,
    isNew,
    workspaceId: params.workspace_id,
    deployModelVersionId: isBatchScoreModel ? params.item_name : undefined,
  };

  const mlopsApiVersionQuery = useQueryMlopsApiVersion();
  const mlopsApiVersion = mlopsApiVersionQuery.data || FALLBACK_MLOPS_API_VERSION;
  const isMlopsApiV1_0_x = semver.satisfies(mlopsApiVersion, '1.0.x');

  const monitoringTabProps: IBatchScoringTabMonitoringProps | undefined =
    monitoringGloballyDisabled || !job?.monitoringOptions?.enabled || isMlopsApiV1_0_x
      ? undefined
      : {
          jobId: getIdFromName(job?.name) || '',
          experimentId: experimentId,
          jobCreationTime: job?.createTime ? new Date(job?.createTime) : undefined,
          columnNames: job?.monitoringOptions?.columns?.map((col) => col.column),
          workspaceId: params.workspace_id,
        };

  const selectedTab = pathTabToJobTab[(params.tab_id || ``) as keyof typeof pathTabToJobTab];
  const onTabClick = React.useCallback(
    (tab: JobTab) => {
      const tabWithRedirect =
        tab === JobTab.MONITORING && (monitoringGloballyDisabled || !job?.monitoringOptions?.enabled)
          ? JobTab.DETAILS
          : tab; // If monitoring is not enabled, redirect to details tab.
      const tabPath = Object.keys(pathTabToJobTab).find(
        (key) => pathTabToJobTab[key as keyof typeof pathTabToJobTab] === tabWithRedirect
      );
      history.push(`/mlops/workspaces/${params.workspace_id}/jobs/${params.job_id}/${tabPath || ''}`);
    },
    [job?.monitoringOptions?.enabled, history, monitoringGloballyDisabled, params.job_id, params.workspace_id]
  );

  useHaicPageTitle(`${isNew ? 'Start New Job' : job?.displayName || 'Job Details'} | MLOps`, env?.cloudInstanceName);

  if (showNotFound) {
    return MLOpsError404Page(`/mlops/workspaces/${params.workspace_id}/jobs`, 'Return to all jobs');
  }

  return (
    <NavigationWrapper>
      <PageWrapper>
        <HeaderPure items={breadcrumbs} pageTitle={isNew ? 'Start new job' : job?.displayName || 'Job detail'} />
        {isNew || (!isNew && !job?.monitoringOptions?.enabled) || isMlopsApiV1_0_x ? (
          loading ? (
            <LoaderView loaderText="Loading..." />
          ) : (
            <BatchScoringTabDetail {...detailTabProps} />
          )
        ) : (
          <JobDetailContent
            detailTabProps={detailTabProps}
            monitoringTabProps={monitoringTabProps}
            onTabClick={onTabClick}
            selectedTab={selectedTab}
            workspaceId={params.workspace_id}
            loading={loading}
          />
        )}
      </PageWrapper>
    </NavigationWrapper>
  );
};

export default BatchScoringDetail;
